import { useState, useEffect, type ChangeEvent } from 'react';
import { apiFetch } from '@/utils/api';
import { useNotify } from '@/context/NotificationContext';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { Trash2, Plus, Image as ImageIcon, Save, X, ChevronLeft, RefreshCw, Sparkles, DollarSign, Upload } from 'lucide-react';

interface SkinsManagerProps {
    onBack: () => void;
}

export const WalletCardsManager = ({ onBack }: SkinsManagerProps) => {
    const { showNotification } = useNotify();
    const { impactOccurred, notificationOccurred } = useHapticFeedback();

    const [skins, setSkins] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [refreshSeed, setRefreshSeed] = useState(Date.now());
    const [isSaving, setIsSaving] = useState(false);

    // Form State
    const [newName, setNewName] = useState('');
    const [newDesc, setNewDesc] = useState('');
    const [newPrice, setNewPrice] = useState('0');
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [isFree, setIsFree] = useState(false);
    const [isPremium, setIsPremium] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        try {
            const skinsRes = await apiFetch('/api/skins');
            const skinsData = await skinsRes.json();
            
            // Filter to show only wallet_card type
            if (skinsData.success) {
                const walletCards = skinsData.skins.filter((s:any) => s.description === 'wallet_card');
                setSkins(walletCards);
            }
        } catch (e) {
            console.error(e);
            showNotification('Error', 'Failed to fetch data', 'error');
        } finally {
            setLoading(false);
            setRefreshSeed(Date.now());
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleAdd = async () => {
        if (isSaving) return;
        if (!newName || !imageFile) {
            notificationOccurred('error');
            return showNotification('Error', 'Name and Image file are required', 'error');
        }

        const formData = new FormData();
        formData.append('name', newName);
        formData.append('image', imageFile);
        formData.append('price', isFree ? '0' : newPrice);
        formData.append('is_free', String(isFree));
        formData.append('is_premium', String(isPremium));
        formData.append('description', 'wallet_card');
        formData.append('skin_type', 'wallet_card');

        setIsSaving(true);
        try {
            const res = await apiFetch('/api/skins/add', {
                method: 'POST',
                body: formData
            });

            const data = await res.json();
            if (data.success) {
                impactOccurred('medium');
                showNotification('Success', 'Item added successfully', 'success');
                setShowAddModal(false);
                resetForm();
                fetchData();
            } else {
                notificationOccurred('error');
                showNotification('Error', data.error || 'Failed to add item', 'error');
            }
        } catch (e) {
            console.error(e);
            notificationOccurred('error');
            showNotification('Error', 'Network error', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const resetForm = () => {
        setNewName('');
        setNewDesc('');
        setNewPrice('0');
        setImageFile(null);
        setPreviewUrl(null);
        setIsFree(false);
        setIsPremium(false);
    };

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setImageFile(file);
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button onClick={onBack} className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                        <ChevronLeft size={20} className="text-white/60" />
                    </button>
                    <div>
                        <h2 className="text-xl font-bold text-white">Wallet Cards Manager</h2>
                        <p className="text-xs text-white/40">Manage marketplace wallet card covers</p>
                    </div>
                </div>
                <button onClick={() => { resetForm(); setShowAddModal(true); }} className="bg-[#3390EC] hover:bg-[#3390EC]/80 text-white px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-all active:scale-95">
                    <Plus size={16} /> Add Wallet Card
                </button>
            </div>

            {loading ? (
                <div className="text-center py-12 text-white/40 animate-pulse">Loading data...</div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {skins.map(skin => (
                        <div key={skin.id} className="relative group bg-[#1c1c1e] rounded-xl overflow-hidden border border-white/5 hover:border-white/10 transition-all">
                            <div className="aspect-[3/1] bg-black/50 relative">
                                {skin.image_url.endsWith('.mp4') || skin.image_url.endsWith('.webm') ? (
                                    <video src={`${skin.image_url}?refresh=${refreshSeed}`} className="w-full h-full object-cover" autoPlay loop muted playsInline />
                                ) : (
                                    <img src={`${skin.image_url}?refresh=${refreshSeed}`} alt={skin.name} className="w-full h-full object-cover" />
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-60" />
                                <div className="absolute top-2 right-2 flex gap-1">
                                    {skin.is_premium && <span className="bg-amber-500 text-black text-[9px] font-bold px-1.5 py-0.5 rounded">PREMIUM</span>}
                                    {skin.is_free && <span className="bg-green-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">FREE</span>}
                                </div>
                            </div>
                            <div className="p-3">
                                <h3 className="font-bold text-sm text-white truncate">{skin.name}</h3>
                                <div className="flex items-center justify-between mt-1">
                                    <span className="text-xs text-blue-400 font-mono">{skin.is_free ? 'Free' : `${skin.price} GRAM`}</span>
                                    <span className={`text-[10px] uppercase font-bold ${skin.is_active ? 'text-green-500' : 'text-red-500'}`}>
                                        {skin.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                    {skins.length === 0 && (
                        <div className="col-span-full py-12 text-center border-2 border-dashed border-white/5 rounded-2xl text-white/20">
                            No wallet cards found. Add one to get started.
                        </div>
                    )}
                </div>
            )}

            {showAddModal && renderAddModal()}
        </div>
    );

    function renderAddModal() {
        return (
            <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
                <div className="bg-[#111113] w-full max-w-lg rounded-[32px] border border-white/10 shadow-[0_0_100px_rgba(51,144,236,0.15)] overflow-hidden animate-in slide-in-from-bottom-8 zoom-in-95 duration-300 relative">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-[80px] -z-10 pointer-events-none" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/10 rounded-full blur-[80px] -z-10 pointer-events-none" />

                    <div className="p-6 border-b border-white/5 flex items-center justify-between relative z-10">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                                <ImageIcon size={20} className="text-white" />
                            </div>
                            <div>
                                <h3 className="font-black text-xl text-white tracking-tight">Create Wallet Card Cover</h3>
                                <p className="text-[10px] text-white/50 uppercase tracking-widest font-bold">Wallet Cards Manager</p>
                            </div>
                        </div>
                        <button onClick={() => setShowAddModal(false)} className="w-8 h-8 flex items-center justify-center bg-white/5 hover:bg-white/10 rounded-full transition-colors">
                            <X size={16} className="text-white/60" />
                        </button>
                    </div>

                    <div className="p-6 space-y-6 relative z-10">
                        <div className="space-y-4">
                            <div className="relative group">
                                <input value={newName} onChange={e => setNewName(e.target.value)} placeholder=" " className="peer w-full bg-[#1c1c1e] border border-white/5 rounded-2xl px-5 py-4 text-sm text-white focus:border-blue-500 focus:bg-[#202022] outline-none transition-all placeholder-transparent" />
                                <label className="absolute left-5 top-4 text-xs font-bold text-white/40 uppercase transition-all peer-focus:-top-2.5 peer-focus:text-[10px] peer-focus:text-blue-400 peer-focus:bg-[#111113] peer-focus:px-2 peer-[:not(:placeholder-shown)]:-top-2.5 peer-[:not(:placeholder-shown)]:text-[10px] peer-[:not(:placeholder-shown)]:text-white/60 peer-[:not(:placeholder-shown)]:bg-[#111113] peer-[:not(:placeholder-shown)]:px-2 pointer-events-none">Card Cover Name</label>
                            </div>
                        </div>

                        <div>
                            <div className="relative group cursor-pointer overflow-hidden rounded-2xl border-2 border-dashed border-white/10 hover:border-blue-500/50 bg-[#1c1c1e]/50 hover:bg-[#1c1c1e] transition-all">
                                <input type="file" accept="image/*,video/mp4,video/webm" onChange={handleFileChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20" />
                                {previewUrl ? (
                                    <div className="relative aspect-[21/9] w-full">
                                        {imageFile?.type.startsWith('video/') ? (
                                            <video src={previewUrl} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" autoPlay loop muted playsInline />
                                        ) : (
                                            <img src={previewUrl} alt="Preview" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                                        )}
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#111113] to-transparent" />
                                        <div className="absolute flex items-center justify-center inset-0 z-10 pointer-events-none">
                                            <span className="bg-black/50 backdrop-blur-md text-white text-xs font-bold px-4 py-2 rounded-full border border-white/10">Change Image/Video</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="px-5 py-10 flex flex-col items-center justify-center gap-3">
                                        <div className="w-14 h-14 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-400 group-hover:scale-110 group-hover:bg-blue-500/20 transition-all">
                                            <Upload size={24} />
                                        </div>
                                        <div className="text-center">
                                            <p className="text-sm font-bold text-white mb-1">Upload Asset</p>
                                            <p className="text-[10px] text-white/40 uppercase tracking-widest">JPG/PNG/WEBP/MP4 (Max 20MB)</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <label className={`flex flex-col items-center justify-center gap-2 p-4 rounded-2xl border cursor-pointer transition-all ${isFree ? 'bg-green-500/10 border-green-500/30' : 'bg-[#1c1c1e] border-white/5'}`}>
                                    <input type="checkbox" checked={isFree} onChange={e => { setIsFree(e.target.checked); if (e.target.checked) setNewPrice('0'); }} className="hidden" />
                                    <span className={`text-[10px] uppercase font-black tracking-widest ${isFree ? 'text-green-500' : 'text-white/40'}`}>Free Drop</span>
                                </label>

                                <label className={`flex flex-col items-center justify-center gap-2 p-4 rounded-2xl border cursor-pointer transition-all ${isPremium ? 'bg-amber-500/10 border-amber-500/30' : 'bg-[#1c1c1e] border-white/5'}`}>
                                    <input type="checkbox" checked={isPremium} onChange={e => setIsPremium(e.target.checked)} className="hidden" />
                                    <span className={`text-[10px] uppercase font-black tracking-widest ${isPremium ? 'text-amber-500' : 'text-white/40'}`}>Premium</span>
                                </label>
                                
                                {!isFree && (
                                    <div className="col-span-2 relative group">
                                        <input type="number" value={newPrice} onChange={e => setNewPrice(e.target.value)} placeholder=" " className="peer w-full bg-[#1c1c1e] border border-white/5 rounded-2xl px-5 py-4 pl-12 text-lg font-bold text-white focus:border-blue-500 focus:bg-[#202022] outline-none transition-all placeholder-transparent" />
                                        <div className="absolute left-5 top-[18px] text-blue-500 pointer-events-none"><DollarSign size={20} /></div>
                                        <label className="absolute left-12 top-4.5 text-xs font-bold text-white/40 uppercase transition-all peer-focus:-top-2.5 peer-focus:left-5 peer-focus:text-[10px] peer-focus:text-blue-400 peer-focus:bg-[#111113] peer-focus:px-2 peer-[:not(:placeholder-shown)]:-top-2.5 peer-[:not(:placeholder-shown)]:left-5 peer-[:not(:placeholder-shown)]:text-[10px] peer-[:not(:placeholder-shown)]:text-white/60 peer-[:not(:placeholder-shown)]:bg-[#111113] peer-[:not(:placeholder-shown)]:px-2 pointer-events-none">Price (GRAM)</label>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="p-6 pt-2 relative z-10 flex gap-3">
                        <button disabled={isSaving} onClick={handleAdd} className={`w-full py-4 rounded-2xl font-bold text-white transition-all flex items-center justify-center gap-2 ${isSaving ? 'bg-white/10 text-white/40 cursor-not-allowed' : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:opacity-90 shadow-lg shadow-blue-500/25 active:scale-[0.98]'}`}>
                            {isSaving ? <RefreshCw size={20} className="animate-spin" /> : <Sparkles size={20} />}
                            {isSaving ? 'Deploying...' : 'Publish Wallet Card'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
};
